/**
 * Site Plan Mini Card Plugin for Search Results Map
 *
 * Renders a desktop tooltip card when the user hovers over a site plan area
 * (both static image and interactive SVG layer site plans).
 *
 * The card is identical to the one rendered by miniMapCard.plugin for marker
 * hover, and reuses the same DOM helpers and card building infrastructure.
 * The plugin subscribes to 'sitePlanHovered' / 'sitePlanHoverEnded' custom
 * DOM events dispatched by the site plan hover handlers so it stays completely
 * decoupled from sitePlan.plugin and interactiveSitePlan.plugin.
 *
 * Single responsibility: show and dismiss the desktop hover tooltip for site plans.
 *
 * This plugin does NOT:
 * - Run on mobile (defers to sitePlanCarousel.plugin)
 * - Handle marker cards (see miniMapCard.plugin)
 * - Manage site plan overlay lifecycles (see sitePlan.plugin, interactiveSitePlan.plugin)
 * - Handle navigation or analytics (see handlers/)
 *
 * Zoom gate: cards are only shown within [MIN_ZOOM, MAX_ZOOM] to avoid
 * showing tooltips when site plans are not active (below MIN_ZOOM) and to
 * avoid covering details at high zoom (above MAX_ZOOM).
 *
 * Reason to change: tooltip positioning logic, zoom threshold, card overlay
 * lifecycle, or the desktop card rendering path for site plans.
 *
 * @module searchResultsMap/plugins/sitePlanMiniCard.plugin
 */

import { sitePlanHovered$, sitePlanHoverEnded$ } from '../redux/observables';
import { selectMapResults, selectSitePlans } from '../redux/selectors';
import { mapMarkerToCardInformation } from '../services/mapper';
import { resolveTooltipDirection } from '../services/cardBuilder';
import {
  createCardOverlay,
  buildCardElement,
  mountCardAtGeoPosition,
  clearMiniMapCards,
} from '../components/miniMapCardDom';
import {
  setCommunityActiveState,
} from '../components/sitePlanMiniCardDom';
import MarkerType from '../enums/markerType';

/**
 * Zoom level above which site plan mini cards are suppressed.
 * At very high zoom the site plan fills the viewport and a card would obstruct it.
 * Adjust this constant to tune the threshold without touching plugin logic.
 */
const MIN_ZOOM = 14;
const MAX_ZOOM = 17;
const HOVER_END_DISMISS_DELAY_MS = 80;
const HOVER_TRANSITION_SELECTOR = '[data-site-plan-community-id]';

/**
 * Creates the site plan mini card plugin.
 *
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createSitePlanMiniCardPlugin() {
  const pluginState = {
    started: false,
    mapFacade: null,
    mapContainer: null,
    cardOverlay: null,
    isDesktopBreakpoint: null,
    getState: null,
    hasInteractiveSiteMap: false,
    hoveredCommunityId: null,
    isHoveringCard: false,
    cardElement: null,
    dismissTimeoutId: null,
    mapEventUnsubscribers: [],
    subscriptions: [],
  };

  function clearDismissTimeout() {
    if (pluginState.dismissTimeoutId === null) {
      return;
    }
    clearTimeout(pluginState.dismissTimeoutId);
    pluginState.dismissTimeoutId = null;
  }

  function handleCardMouseEnter() {
    pluginState.isHoveringCard = true;
    clearDismissTimeout();
    setCommunityActiveState(pluginState.hoveredCommunityId, true);
  }

  function disposeCardElement() {
    if (!pluginState.cardElement) {
      return;
    }
    pluginState.cardElement.remove();
    pluginState.cardElement.removeEventListener('mouseenter', handleCardMouseEnter);
    pluginState.cardElement = null;
  }

  function clearCardAndHoverState() {
    disposeCardElement();
    setCommunityActiveState(pluginState.hoveredCommunityId, false);
    pluginState.hoveredCommunityId = null;
    pluginState.isHoveringCard = false;
    pluginState.hasInteractiveSiteMap = false;
  }

  function isZoomWithinCardRange(zoom) {
    return zoom >= MIN_ZOOM && zoom <= MAX_ZOOM;
  }

  function isDesktopBreakpoint() {
    return pluginState.isDesktopBreakpoint?.() ?? false;
  }

  function findCommunityResult(communityId) {
    if (!communityId) {
      return null;
    }
    const results = selectMapResults(pluginState.getState()) ?? [];
    return results.find((result) =>
      result.communityId === communityId &&
      (result.type === MarkerType.HousingDevelopment.toString() ||
        result.type === MarkerType.BuildablePlan.toString()));
  }

  function scheduleCardDismiss() {
    clearDismissTimeout();
    pluginState.dismissTimeoutId = setTimeout(() => {
      pluginState.dismissTimeoutId = null;
      if (pluginState.isHoveringCard) {
        return;
      }
      clearCardAndHoverState();
    }, HOVER_END_DISMISS_DELAY_MS);
  }

  function isHoverTransitionTarget(target) {
    return !!target?.closest?.(HOVER_TRANSITION_SELECTOR);
  }

  function getSitePlanCenterPosition(communityId, fallbackPosition) {
    const sitePlans = selectSitePlans(pluginState.getState()) ?? [];
    const communityPlans = sitePlans.filter((sitePlan) => sitePlan.communityId === communityId);

    if (!communityPlans.length) {
      return fallbackPosition;
    }

    // Pick the largest precomputed site plan area for this community.
    const largestPlan = communityPlans.reduce((largest, current) => {
      const currentArea = current.area ?? 0;
      const largestArea = largest.area ?? 0;
      return currentArea > largestArea ? current : largest;
    });

    const center = largestPlan.centerPosition;
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
      return center;
    }

    return fallbackPosition;
  }

  function clampPositionToBounds(position, bounds) {
    if (!position || !bounds) {
      return null;
    }

    const lat = Number(position.lat);
    const lng = Number(position.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return {
      lat: Math.min(bounds.north, Math.max(bounds.south, lat)),
      lng: Math.min(bounds.east, Math.max(bounds.west, lng)),
    };
  }

  function handleCardMouseLeave(event) {
    pluginState.isHoveringCard = false;

    if (isHoverTransitionTarget(event.relatedTarget)) {
      return;
    }

    clearCardAndHoverState();
  }

  function renderCard(result, communityId) {
    if (!result || !pluginState.mapContainer || !pluginState.cardOverlay) {
      return;
    }

    const snapshot = pluginState.mapFacade?.getCameraSnapshot?.() ?? null;
    if (!snapshot || !isZoomWithinCardRange(snapshot.zoom ?? 0)) {
      disposeCardElement();
      return;
    }

    disposeCardElement();

    const cardData = { ...mapMarkerToCardInformation(result), type: result.type };
    const cardElement = buildCardElement(cardData, handleCardMouseLeave, handleCardMouseEnter);
    if (!cardElement) {
      return;
    }

    pluginState.cardElement = cardElement;
    clearMiniMapCards(pluginState.mapContainer, pluginState.cardElement);

    const bounds = snapshot.bounds ?? pluginState.mapFacade?.getBounds?.();
    if (!bounds) {
      return;
    }

    const position = getSitePlanCenterPosition(communityId, result.position);
    const boundedPosition = clampPositionToBounds(position, bounds);
    if (!boundedPosition) {
      return;
    }

    const direction = resolveTooltipDirection(bounds, boundedPosition);
    mountCardAtGeoPosition(
      pluginState.cardElement,
      boundedPosition,
      bounds,
      pluginState.mapContainer,
      pluginState.cardOverlay,
      direction,
    );
  }

  function handleSitePlanHovered({ communityId }) {
    if (!isDesktopBreakpoint()) {
      return;
    }

    if (!communityId) {
      return;
    }

    const resultMatch = findCommunityResult(communityId);
    if (!resultMatch) {
      return;
    }

    clearDismissTimeout();

    if (pluginState.hoveredCommunityId && pluginState.hoveredCommunityId !== communityId) {
      setCommunityActiveState(pluginState.hoveredCommunityId, false);
    }

    pluginState.hoveredCommunityId = communityId;
    pluginState.isHoveringCard = false;
    pluginState.hasInteractiveSiteMap = !!resultMatch.hasInteractiveSiteMap;

    setCommunityActiveState(communityId, true);

    renderCard(resultMatch, communityId);
  }

  function handleSitePlanHoverEnded({ communityId }) {

    if (communityId && pluginState.hoveredCommunityId && communityId !== pluginState.hoveredCommunityId) {
      return;
    }

    if (pluginState.isHoveringCard) {
      return;
    }

    scheduleCardDismiss();
  }

  function handleZoomChanged() {
    const snapshot = pluginState.mapFacade?.getCameraSnapshot?.() ?? null;
    if (!snapshot) {
      return;
    }

    if (!isZoomWithinCardRange(snapshot.zoom ?? 0)) {
      clearCardAndHoverState();
    }
  }

  function setupSubscriptions() {
    pluginState.subscriptions.push(
      sitePlanHovered$.subscribe(handleSitePlanHovered),
      sitePlanHoverEnded$.subscribe(handleSitePlanHoverEnded),
    );

    if (pluginState.mapFacade) {
      pluginState.mapEventUnsubscribers.push(
        pluginState.mapFacade.onMapEvent('zoom_changed', handleZoomChanged),
      );
    }
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((sub) => sub?.unsubscribe?.());
    pluginState.subscriptions = [];
    pluginState.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
    pluginState.mapEventUnsubscribers = [];
    clearDismissTimeout();
  }

  return {
    name: 'site-plan-mini-card',

    async init(context) {
      context.state.sitePlanMiniCard = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.mapFacade = context.services.mapFacade;
      pluginState.mapContainer = context.root;
      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;
      pluginState.getState = context.services.getState;

      if (!pluginState.mapContainer) {
        return;
      }

      pluginState.cardOverlay = createCardOverlay(pluginState.mapContainer);
      setupSubscriptions();

      pluginState.started = true;
      context.state.sitePlanMiniCard = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      clearCardAndHoverState();

      pluginState.cardOverlay?.remove();
      pluginState.cardOverlay = null;

      pluginState.mapFacade = null;
      pluginState.mapContainer = null;
      pluginState.isDesktopBreakpoint = null;
      pluginState.getState = null;
      pluginState.started = false;

      delete context.state.sitePlanMiniCard;
    },
  };
}
